import axios from "axios";
import { GEEKS_USER } from "constants/profile";
import { getDataFromLocalStorage, localUserExist } from "helpers/generalHelper";
import { toast } from "helpers/generalHelper";
import * as Sentry from "@sentry/react";

// Get token from local storage
const getToken = () => {
  return getDataFromLocalStorage(GEEKS_USER)?.token || null;
};

// Common Axios Instance Config
const axiosConfig = {
  baseURL: process.env.REACT_APP_BASE_URL, // Development
};

// Create Default Axios Instances
const instance = axios.create(axiosConfig);
const instanceWithoutInterceptors = axios.create(axiosConfig);

// Interceptor for adding common headers to requests without authorization
instanceWithoutInterceptors.interceptors.request.use((config) => {
  config.headers["Accept-Language"] = "en"; // Set content language header
  return config;
});

// Interceptor to add Authorization token for authenticated requests
instance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`; // Set Authorization header
    if (config.responseType !== "blob" && !(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
      config.headers["Accept"] = "application/json";
    }
  }
  return config;
});

// Add interceptors for validate authorization
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { status, data } = error?.response || {};
    const url = error?.config?.url;
    const env = process.env.REACT_APP_NODE_ENV;
    const sentryEnabled = !!process.env.REACT_APP_SENTRY_DNS;
    const isProductionOrStaging = ["production", "staging"].includes(env);

    // --- Capture server error in Sentry ---
    if (sentryEnabled && status === 500 && isProductionOrStaging) {
      Sentry.captureException(error);
    }

    // --- Handle unauthorized access first so an expired session always
    // redirects to login, regardless of which endpoint returned 401 ---
    if (status === 401 && localUserExist()) {
      localStorage.clear();
      window.location.href = "/login";
      return Promise.reject(new Error(error));
    }

    // --- Handle specific validation and lock errors ---
    const isSuggestionError =
      status === 422 && url === "/team-run/trade/companies/suggestions";
    // Lookup is optional until the route exists. A 404 must not toast on each keystroke.
    const isTraderSuggestionMiss =
      status === 404 && url === "/team-run/trade/traders/suggestions";
    const isImportCsvError = url === "/option-rows/import-csv";
    const isQuickAddError =
      url === "/option-rows/quick-add" ||
      url?.startsWith("/option-rows/quick-add/drafts");
    const isCreateTenorPost =
      error?.config?.method?.toLowerCase() === "post" &&
      url === "/option-rows/tenors";
    // Study telemetry — never toast; caller swallows the resolved error.
    const isQuickAddShorthandLog = url === "/quick-add/shorthand-logs";
    const isCalculationFormulasError =
      status === 422 && url?.includes("/calculation-formulas");
    const isFuturesCurveSaveRequest =
      error?.config?.method?.toLowerCase() === "post" &&
      url === "/vol-surface/futures-curve/control-table";
    const isLockError =
      status === 423 &&
      [
        "/vol-surface/strike-surfaces/control-table/update",
        "/vol-surface/other-vol-surfaces/update",
        "/vol-surface/other-vol-surfaces/set-strikes",
        "/vol-surface/other-vol-surfaces/set-strikes/apply",
        "/vol-surface/delta-surfaces/control-table",
        "/vol-surface/delta-surfaces/control-table/vol-calc-table",
      ].includes(url);

    const isScreenshotImportRequest = [
      "/screen-capture",
      "/screenshot-imports",
      "/trade-capture",
    ].some((prefix) => url?.startsWith(prefix));

    // Import flows resolve with the error response so callers can branch on
    // `vol_calculation_failed` / `rows_with_errors` and toast themselves.
    if (
      isSuggestionError ||
      isTraderSuggestionMiss ||
      isLockError ||
      isScreenshotImportRequest ||
      isImportCsvError ||
      isQuickAddError ||
      isQuickAddShorthandLog
    ) {
      return error?.response;
    }

    // Futures curve save: caller toasts full API message (skip global toast).
    if (isFuturesCurveSaveRequest) {
      return Promise.reject(error);
    }

    // Add Tenor POST 422: keep the Axios error so the caller can toast Laravel
    // `message` / `errors`. Rejecting `new Error(error)` would strip `.response`.
    if (status === 422 && isCreateTenorPost) {
      return Promise.reject(error);
    }

    if (isCalculationFormulasError) {
      return Promise.reject(error);
    }

    // --- Handle general validation error ---
    // Prefer the first field message (e.g. option_type_id) over Laravel's generic
    // "The given data was invalid." so Pricer type-change and similar edits surface
    // the actionable reason.
    if (status === 422) {
      const firstFieldMessage = data?.errors
        ? Object.values(data.errors).flat().find(Boolean)
        : null;
      toast(firstFieldMessage || data?.message, "warning");
    }

    // --- Handle other errors unless request was cancelled ---
    if (status !== 422 && error.code !== "ERR_CANCELED") {
      toast(data?.message || "Backend Error", "error");
    }

    return Promise.reject(new Error(error));
  }
);

// Export Axios with Auth Headers
export default instance;

// Export Axios Without instance
export { instanceWithoutInterceptors as axiosWI };
